import React, { useEffect, useState } from "react";
import { ChatReceive, ChatSend } from "./Chat/ChatBody";
import { getProfile } from "../actions/login";
import { getProfileById } from "../actions/userprofile";
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../Utils/firebase";
import SendChat from "./Chat/SendChat";
import ReceiveChat from "./Chat/ReceiveChat";
import { toast } from "react-toastify";
import { api } from "../actions/api";
import ChatMessageBox from "./Chat/ChatMessageBox";
import { FIREBASE_CONVERSATIONS } from "../Utils/constant";
import FilePreviewModal from "./Chat/FilePreviewModal";
import { generateThumbnail } from "../helpers/GenerateThumbnailHelper";
import convertToMp3 from "../Utils/convertToMp3";
import { formatTime } from "../Utils/helpers";
import JournalPreviewInThread from "./JournalPreviewInThread";
import useUserStore from "../zustand-stores/UserStore";
import { getFirestoreUsers } from "../hooks/useFireStore";
import { sendFcmNotificationToTopic } from "../helpers/NotificationHelper";

// import { getFirestoreUsers } from "../hooks/useFireStore";

const ThreadChat = ({
  profileImage,
  username,
  unit,
  currentUserId,
  threadId,
  conversationId,
  threadParticipants,
}) => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [audioChunks, setAudioChunks] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [latest5MessagesOfUsers, setLatest5MessagesOfUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [mainMessage, setMainMessage] = useState(null);
  const [mainOwner, setMainOwner] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [disabledEditorButton, setDisabledEditorButton] = useState(false);

  const { user } = useUserStore();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const profile = await getProfile();
        setUserProfile(profile);
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };

    fetchUserProfile();
    fetchMessages();
  }, []);

  useEffect(() => {
    if (mainMessage) {
      const fetchMainUserProfile = async () => {
        let mainOwnerInstance = await getProfileById(mainMessage.senderId);

        if (mainOwnerInstance) setMainOwner(mainOwnerInstance);
      };
      fetchMainUserProfile();
    }
  }, [mainMessage]);

  const fetchMessages = async () => {
    if (!threadId) {
      setLoading(false);
      return;
    }

    const threadRef = doc(db, "threads", threadId);
    getDoc(threadRef).then((threadSnap) => {
      if (threadSnap.exists()) {
        const threadData = threadSnap.data();
        setMainMessage(threadData.mainMessage);
      } else {
        console.error("Conversation not found!");
      }
    });

    const messagesCollectionRef = collection(
      db,
      "threads",
      threadId,
      "messages",
    );
    const q = query(messagesCollectionRef, orderBy("timestamp", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const msgs = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMessages(msgs);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching messages:", error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
  };

  const handleFileClick = (file) => {
    setPreviewFile(file);
  };

  const chunkSize = 5 * 1024 * 1024; // 5MB per chunk

  function generateRandomString(length) {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  const uploadFileInChunks = async (file, fileId) => {
    const totalChunks = Math.ceil(file.size / chunkSize);

    for (let chunkNumber = 1; chunkNumber <= totalChunks; chunkNumber++) {
      const start = (chunkNumber - 1) * chunkSize;
      const end = Math.min(file.size, start + chunkSize);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append("file_id", fileId);
      formData.append("chunk_number", chunkNumber);
      formData.append("total_chunks", totalChunks);
      formData.append("file_name", file.name);
      formData.append("chunk", chunk);
      await api.post("/upload-chunk", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
  };

  const handleSendMessage = async (content, file, mentionedIds = []) => {
    setLoading(true);

    let fileData = null;

    if (file) {
      fileData = await handleSendFile(file);
    }

    if (!content && !fileData) {
      return toast.error("Enter Message");
    }

    // const msg = content;

    // setMessage("")

    try {
      const messageData = {
        type: "text",
        metadata: {},
        reactions: {},
        isRead: null,
        senderId: userProfile?.id,
        senderName: userProfile?.full_name,
        senderProfileImage: userProfile?.image,
        timestamp: serverTimestamp(),
        content: content,
        isFile: !!fileData, // true or false
        mentionedIds: mentionedIds || [],
      };

      if (fileData) {
        messageData.file = {
          thumbnail: fileData.media_url || null,
          url:
            fileData.video_url ||
            fileData.media_url ||
            fileData.audio_url ||
            null,
          type: fileData.type || null,
        };
      }

      const newMessageRef = await addDoc(
        collection(db, "threads", threadId, "messages"),
        messageData,
      );

      // 2) Transaction to update conversation (lastMessage + unreadCount)
      const convoRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);

      await runTransaction(db, async (transaction) => {
        // 1 Read conversation
        const convoSnap = await transaction.get(convoRef);
        if (!convoSnap.exists()) return;

        // 2 Read original message
        const mainMessageId = threadId.slice(threadId.lastIndexOf("_") + 1);
        const originalMessageRef = doc(
          db,
          FIREBASE_CONVERSATIONS,
          conversationId,
          "messages",
          mainMessageId,
        );
        const originalSnap = await transaction.get(originalMessageRef);

        // 3 Compute updated data
        const convoData = convoSnap.data();
        const currentUnread = convoData.unreadCount || {};
        // Use only participants (not visibleTo) to avoid re-adding removed members
        const allParticipants = (convoData.participants || []).filter(Boolean);

        const updatedUnread = { ...currentUnread };

        // Ensure every participant has a key and increment if they pass sanity check
        allParticipants.forEach((uid) => {
          const uidStr = String(uid);

          const isNotSender = uidStr !== String(userProfile.id);
          const isVisible = (convoData.visibleTo || [])
            .map(String)
            .includes(uidStr);
          const isInDetails =
            convoData.type === "group"
              ? convoData.participantDetails &&
                uidStr in convoData.participantDetails
              : true;

          if (isNotSender && isVisible && isInDetails) {
            if (!(uidStr in updatedUnread)) {
              updatedUnread[uidStr] = 0;
            }
            updatedUnread[uidStr] = (updatedUnread[uidStr] || 0) + 1;
          }
        });

        const unreadUserIds = Object.keys(updatedUnread).filter(
          (uid) => updatedUnread[uid] > 0,
        );

        const currentReplyTo = originalSnap?.data()?.replyTo || { count: 0 };
        const previewMessage = content || (fileData ? "Attachment" : "");

        // 4 Update parent conversation
        transaction.update(convoRef, {
          lastMessage: previewMessage,
          lastMessageSenderId: String(userProfile?.id),
          lastMessageTime: serverTimestamp(),
          updatedAt: serverTimestamp(),
          unreadCount: updatedUnread,
          unreadUserIds,
          participants: allParticipants, // Sync the fixed list
          lastMessageThreadId: threadId,
          lastMessageMentionedIds: mentionedIds || [],
        });

        if (originalSnap?.exists()) {
          const replyToUpdate = {
            id: mainMessageId,
            count: (currentReplyTo.count || 0) + 1,
            message: content,
          };

          // Include file info if replying with a file
          if (fileData) {
            replyToUpdate.file = {
              thumbnail: fileData.media_url || null,
              url:
                fileData.video_url ||
                fileData.media_url ||
                fileData.audio_url ||
                null,
              type: fileData.type || null,
            };
          }

          transaction.update(originalMessageRef, {
            replyTo: replyToUpdate,
          });
        }
      });

      const convoSnapAfter = await getDoc(convoRef);
      const convoDataAfter = convoSnapAfter.data();
      const allParticipants = (convoDataAfter.participants || []).filter(
        Boolean,
      );

      // Send Push Notification
      const isGroup = convoDataAfter.type === "group";
      const notificationTitle = isGroup
        ? `${userProfile.full_name} in ${convoDataAfter.metadata?.groupName || "Group"}`
        : `${userProfile.full_name} replied to a thread`;

      sendFcmNotificationToTopic({
        title: notificationTitle,
        body: content || (fileData ? "Attachment" : ""),
        conversationId: conversationId,
        threadId: threadId,
        isGroupChat: isGroup,
        recipientIds: allParticipants,
        senderId: userProfile.id,
        messageId: newMessageRef.id,
      });

      setDisabledEditorButton(false);
    } catch (error) {
      console.error("Failed to send message:", error);
      setDisabledEditorButton(false);
    } finally {
      setLoading(false);
      setDisabledEditorButton(false);
    }
  };

  const handleSendFile = async (file) => {
    setLoading(true);
    setDisabledEditorButton(true);

    try {
      // Convert audio to MP3 before upload when needed
      if (
        file &&
        file.type &&
        file.type.startsWith("audio") &&
        file.type !== "audio/mpeg"
      ) {
        try {
          const mp3File = await convertToMp3(file);
          if (mp3File) file = mp3File;
        } catch (err) {
          console.warn(
            "convertToMp3 failed, proceeding with original file",
            err,
          );
        }
      }
      const formData = new FormData();

      const uploadedVideoIds = [];

      try {
        if (file.type.startsWith("video")) {
          const fileId = generateRandomString(10);
          await uploadFileInChunks(file, fileId);
          uploadedVideoIds.push(fileId);

          // Generate thumbnail for mobile
          const thumbnail = await generateThumbnail(file);

          if (thumbnail) {
            formData.append("media", thumbnail);
          }
        } else if (file.type.startsWith("audio")) {
          formData.append("audio", file);
        } else {
          formData.append("media", file);
        }

        // 2. Append videoIds once after loop
        if (uploadedVideoIds.length > 0) {
          formData.append("videoIds", JSON.stringify(uploadedVideoIds));
        }
      } catch (e) {
        setLoading(false);
        setDisabledEditorButton(false);
      }

      const response = await api.post("/user/chat/media/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (!response.status == 200) {
        throw new Error("Upload failed");
      }

      const fileData = response.data;

      setLoading(false);
      return fileData;
    } catch (error) {
      console.error("Error uploading file:", error);
      setLoading(false);
      setDisabledEditorButton(false);
    }
  };

  const handleEdit = (messageId, oldText) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId ? { ...msg, isEditing: true } : msg,
      ),
    );
  };

  const handleSaveEdit = async (messageId, newContent) => {
    try {
      const messageRef = doc(db, "threads", threadId, "messages", messageId);
      await updateDoc(messageRef, {
        content: newContent,
      });

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, isEditing: false } : msg,
        ),
      );
    } catch (error) {
      console.error("Failed to save edited message:", error);
      // Optionally, you can revert the state change if saving fails
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, isEditing: true } : msg,
        ),
      );
    }
  };

  const handleCancelEdit = (messageId) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId ? { ...msg, isEditing: false } : msg,
      ),
    );
  };

  const handleLike = async (messageId, emoji) => {
    const userId = userProfile.id;
    // const messageRef = doc(db, FIREBASE_CONVERSATIONS, conversationId, "messages", messageId);
    const messageRef = doc(db, "threads", threadId, "messages", messageId);

    try {
      // Get the current message data to check if already liked
      const messageSnap = await getDoc(messageRef);

      if (!messageSnap.exists()) {
        console.error("Message not found");
        return;
      }

      const messageData = messageSnap.data();
      const currentLikes = messageData.reactions?.like || [];

      // Find if user already reacted with a different emoji
      let previousEmoji = null;
      for (const [key, users] of Object.entries(messageData.reactions)) {
        if (users.includes(String(userId))) {
          previousEmoji = key;
          break;
        }
      }

      const updates = {};

      // If clicking the same emoji again → remove it
      if (previousEmoji === emoji) {
        const updatedUsers = messageData.reactions[emoji].filter(
          (id) => id != userId,
        );

        if (updatedUsers.length === 0) {
          updates[`reactions.${emoji}`] = deleteField();
        } else {
          updates[`reactions.${emoji}`] = updatedUsers;
        }

        await updateDoc(messageRef, updates);
        return;
      }

      // If reacted with a different emoji → remove old one
      if (previousEmoji) {
        const updatedUsers = messageData.reactions[previousEmoji].filter(
          (id) => id != userId,
        );

        if (updatedUsers.length === 0) {
          updates[`reactions.${previousEmoji}`] = deleteField();
        } else {
          updates[`reactions.${previousEmoji}`] = updatedUsers;
        }
      }

      // Add user to the new emoji array
      updates[`reactions.${emoji}`] = arrayUnion(String(userId));

      await updateDoc(messageRef, updates);

      // // Add user to the new emoji array
      // await updateDoc(messageRef, {
      //   [`reactions.${emoji}`]: arrayUnion(String(userId)),
      // });

      // const isLiked = currentLikes.includes(String(userId));

      // if (isLiked) {
      //   // If already liked, remove the like
      //   await updateDoc(messageRef, {
      //     "reactions.like": arrayRemove(String(userId)),
      //   });
      // } else {
      //   // Otherwise, add the like
      //   await updateDoc(messageRef, {
      //     "reactions.like": arrayUnion(String(userId)),
      //   });
      // }
    } catch (error) {
      console.error("Error toggling reactions:", error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const messageRef = doc(db, "threads", threadId, "messages", messageId);

      await updateDoc(messageRef, {
        visibleTo: arrayRemove(String(userProfile?.id)),
      });

      const messageSnap = await getDoc(messageRef);
      if (messageSnap.exists()) {
        const data = messageSnap.data();
        if (!data.visibleTo || data.visibleTo.length === 0) {
          await deleteDoc(messageRef);
        }
      }

      // 2. Update local state
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
    } catch (error) {
      console.error("Failed to delete message:", error);
    }
  };

  const handleDownload = async (file) => {
    if (!file?.url) {
      return;
    }

    try {
      const response = await fetch(file.url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const fileExtension = file.url.split(".").pop().split("?")[0]; // e.g., jpg, png
      const fileName = `downloaded-file.${fileExtension}`;

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(link.href), 150);
    } catch (err) {
      console.error("File download error:", err);
    }
  };

  return (
    <>
      <div className="thread-cards mt-1">
        <div className="d-flex">
          <div className="d-flex flex-grow-1 flex-column">
            <div className="d-flex justify-content-between threads-chat-cards align-items-center">
              <div className="d-flex align-items-center gap-3 rounded">
                <div className="position-relative">
                  <img
                    src={mainOwner?.image}
                    alt="Profile"
                    className="rounded-circle"
                    width="48"
                    height="48"
                  />
                </div>
                <div className="d-flex flex-column">
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-body-b1">{mainOwner?.full_name}</span>
                  </div>
                  {/* <small className="text-body-b4">{unit}</small> */}
                </div>
              </div>
            </div>
            <div className="threads-container">
              <div className="message-groups">
                {/* Journal Preview if main message is a journal share */}
                {mainMessage && (
                  <>
                    <JournalPreviewInThread message={mainMessage} />
                    {mainMessage?.journal?.isJournalShared && (
                      <div
                        style={{
                          height: "1px",
                          backgroundColor: "#333",
                          margin: "16px 0",
                        }}
                      />
                    )}
                  </>
                )}

                {messages &&
                  messages.map((msg, index) => {
                    // Skip the first message if it's a journal shared message
                    // The journal preview is already shown via JournalPreviewInThread component
                    if (index === 0 && msg.journal?.isJournalShared) {
                      return null;
                    }

                    const textContent = msg.content; // strip HTML tags
                    // const timeString = new Date(msg.timestamp.seconds * 1000).toLocaleString(); // format timestamp
                    if (String(msg.senderId) !== String(currentUserId)) {
                      return (
                        <SendChat
                          key={index}
                          username={msg.senderName}
                          title={textContent}
                          senderId={msg.senderId}
                          time={formatTime(msg.timestamp)}
                          userImage={mainOwner?.image}
                          autoscroll={false}
                          // botImage="/icons/james.svg"
                          messageId={msg.id}
                          isFile={msg.isFile}
                          fileInfo={msg?.file}
                          fileType={msg?.file?.type}
                          onDownload={() => handleFileClick(msg?.file)}
                          showReply={false}
                          onLike={handleLike}
                          reactionSummary={msg.reactions || {}}
                          isLiked={
                            Array.isArray(msg.reactions?.like)
                              ? msg.reactions.like.length > 0
                              : false
                          }
                        />
                      );
                    } else {
                      return (
                        <ReceiveChat
                          key={index}
                          response={msg.content}
                          responder={msg.senderName}
                          isEditing={msg.isEditing}
                          messageId={msg.id}
                          disableEdit={msg.disableEdit}
                          time={formatTime(msg.timestamp)}
                          onEdit={handleEdit}
                          userImage={userProfile?.image}
                          autoscroll={false}
                          isFile={msg.isFile}
                          fileInfo={msg?.file}
                          fileType={msg?.file?.type}
                          onDownload={() => handleFileClick(msg?.file)}
                          showReply={false}
                          onLike={handleLike}
                          reactionSummary={msg.reactions || {}}
                          isLiked={
                            Array.isArray(msg.reactions?.like)
                              ? msg.reactions.like.length > 0
                              : false
                          }
                          onDelete={handleDeleteMessage}
                        />
                      );
                    }
                  })}

                {/* message type section */}
                {/* <div className="position-relative">
                  <div
                    style={{
                      display: "flex",
                      height: "79px",
                      alignItems: "center",
                      backgroundColor: "#1c2a37",
                      borderRadius: "24px",
                      overflow: "hidden",
                      bottom: "0px",
                      maxWidth: "945px",
                      width: "100%",
                      marginTop: "20px",
                    }}
                  >
                    <div
                      className="threads-chat-boxs"
                      style={{
                        paddingLeft: "24px",
                        paddingRight: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <input
                        className="text-body-b2 journal-type align-items-center"
                        type="text"
                        id="textInput"
                        value={message}
                        onChange={handleMessageChange}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        style={{
                          flexGrow: 1,
                          border: "none",
                          background: "none",
                          padding: "8px",
                          outline: "none",
                          color: "#dadfe4",
                          fontSize: "inherit",
                          transition: "height 0.3s ease-in-out",
                        }}
                        placeholder="Type a reply ...."
                      />
                      <div className="d-flex gap-3 align-items-center sqds-btns">
                        <div id="firstvoiceButton" style={{ cursor: "pointer" }}>
                          <img
                            className="sqds-voice-icon"
                            src="/icons/voice.svg"
                            alt="Voice Input"
                          />
                        </div>

                        <div>
                          <input
                            type="file"
                            id="file-upload"
                            style={{ display: "none" }}
                            accept="image/*"
                            onChange={() => { }}
                          />
                          <img
                            className="sqds-file-up"
                            style={{ cursor: "pointer" }}
                           
                            src="/icons/file-upload.svg"
                            alt=""
                          />
                        </div>

                        <button
                          className="send-btn btn-1-primary"
                          onClick={handleSendMessage}
                        >
                          <img
                            className="sqds-send"
                            src="/icons/send-icon.svg"
                            alt=""
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div> */}
                {!messages?.some((msg) => msg.isEditing) ? (
                  <ChatMessageBox
                    key={threadId}
                    conversationId={threadId}
                    userProfile={userProfile}
                    onSendClick={handleSendMessage}
                    onSendFile={handleSendFile}
                    participants={threadParticipants}
                    // disabled={!isClientReady}
                    disabledButton={disabledEditorButton}
                  />
                ) : (
                  <ChatMessageBox
                    key={threadId}
                    conversationId={threadId}
                    userProfile={userProfile}
                    onSendClick={(newContent) =>
                      handleSaveEdit(
                        messages.find((msg) => msg.isEditing)?.id,
                        newContent,
                      )
                    }
                    initialText={
                      messages.find((msg) => msg.isEditing)?.content || ""
                    }
                    isEditing={true}
                    onCancel={() =>
                      handleCancelEdit(
                        messages.find((msg) => msg.isEditing)?.id,
                      )
                    }
                    // disabled={!isClientReady}
                    showFileUpload={false}
                    participants={threadParticipants}
                    disabledButton={disabledEditorButton}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <FilePreviewModal
        file={previewFile}
        onClose={() => setPreviewFile(null)}
        onDownload={handleDownload}
      />
    </>
  );
};

export default ThreadChat;
